import { useEffect } from 'react';
import { disconnect, signMessage, verifyMessage, getBalance, readContract, simulateContract, writeContract, sendTransaction, waitForTransactionReceipt, multicall, getWalletClient } from '@wagmi/core';
import { walletConfig } from './walletConfig';
import { useConnectModal, useAccountModal, useChainModal, } from '@rainbow-me/rainbowkit';
import { parseEther } from "viem";
import { useAccount } from "wagmi";
import { instance } from "../../index";
import { watchAsset } from "viem/actions";
export const WalletListenerComponent = () => {
    const app = instance.root;
    const { openConnectModal } = useConnectModal();
    const { openAccountModal } = useAccountModal();
    const { openChainModal } = useChainModal();
    const { _useActions } = app;
    const { setAccount, setSignation } = _useActions();
    const account = useAccount();
    const supportedChainIds = walletConfig.chains.map((item) => {
        return item.id;
    });
    useEffect(() => {
        if (account && !account.isConnected) {
            setAccount(null);
            setSignation(null);
        }
        else {
            setAccount(account);
            setSignation(null);
        }
    }, [account.address, account.chainId]);
    const checkConnect = (acc) => {
        const supportedChainIds = walletConfig.chains.map((item) => {
            return item.id;
        });
        if (!acc.isConnected) {
            if (openConnectModal) {
                openConnectModal();
            }
            return false;
        }
        else if (acc.chainId && !supportedChainIds.includes(acc.chainId)) {
            if (openChainModal) {
                openChainModal();
            }
            return false;
        }
        return true;
    };
    useEffect(() => {
        const handleConnect = async () => {
            if (!checkConnect(account))
                return;
            try {
                if (openConnectModal) {
                    openConnectModal();
                }
            }
            catch (error) {
                app.emit('WalletPilet.ConnectedEmit', {
                    state: 'error',
                    error: error.shortMessage
                });
            }
        };
        const handleSwitchChain = async () => {
            try {
                if (openChainModal) {
                    openChainModal();
                }
            }
            catch (error) {
                app.emit('WalletPilet.ChangeChainEmit', {
                    state: 'error',
                    error: error.shortMessage
                });
            }
        };
        const handleDisconnect = async () => {
            try {
                await disconnect(walletConfig);
                app.emit('WalletPilet.DisconnectedEmit', {
                    state: 'success',
                    isDisconnected: true
                });
            }
            catch (error) {
                app.emit('WalletPilet.DisconnectedEmit', {
                    state: 'error',
                    error: error.shortMessage
                });
            }
        };
        const handleAddToken = async ({ address, decimals, symbol }) => {
            if (!checkConnect(account))
                return;
            try {
                const walletClient = await getWalletClient(walletConfig);
                const result = await watchAsset(walletClient, {
                    type: 'ERC20',
                    options: {
                        address: address,
                        decimals: decimals,
                        symbol: symbol,
                    },
                });
                app.emit('WalletPilet.AddTokenEmit', {
                    state: 'success',
                    result: result
                });
            }
            catch (error) {
                app.emit('WalletPilet.AddTokenEmit', {
                    state: 'error',
                    error: error.shortMessage,
                });
            }
        };
        const handleSignMsg = async ({ msg }) => {
            if (!checkConnect(account))
                return;
            try {
                const signResult = await signMessage(walletConfig, {
                    message: msg,
                });
                setSignation(signResult);
                app.emit('WalletPilet.SignedMsg', {
                    state: 'success',
                    result: signResult
                });
            }
            catch (error) {
                app.emit('WalletPilet.SignedMsg', {
                    state: 'error',
                    error: error.shortMessage
                });
            }
        };
        const handleVerifyMessage = async ({ address, message, signature }) => {
            try {
                let result = await verifyMessage(walletConfig, {
                    address: address,
                    message: message,
                    signature: signature
                });
                app.emit('WalletPilet.VerifyMessageEmit', {
                    state: 'success',
                    result: result
                });
            }
            catch (error) {
                app.emit('WalletPilet.VerifyMessageEmit', {
                    state: 'error',
                    error: error.shortMessage,
                });
            }
        };
        const handleGetBalance = async ({ address, token, eventKey, chainID }) => {
            try {
                const balance = await getBalance(walletConfig, {
                    address: address,
                    token: token ? token : '',
                    chainId: chainID ? chainID : ''
                });
                app.emit('WalletPilet.GetBalanceEmit', {
                    state: 'success',
                    result: balance,
                    eventKey: eventKey
                });
            }
            catch (error) {
                app.emit('WalletPilet.GetBalanceEmit', {
                    state: 'error',
                    error: error.shortMessage,
                    eventKey: eventKey
                });
            }
        };
        const handleReadContract = async ({ abi, address, fn, args, eventKey }) => {
            if (!checkConnect(account))
                return;
            try {
                const result = await readContract(walletConfig, {
                    abi: abi,
                    address: address,
                    functionName: fn,
                    args: args,
                });
                app.emit('WalletPilet.ReadedEmit', {
                    result: result,
                    eventKey: eventKey
                });
            }
            catch (error) {
                app.emit('WalletPilet.ReadedEmit', {
                    state: 'error',
                    eventKey: eventKey,
                    error: error.shortMessage
                });
            }
        };
        const handleWriteContract = async ({ abi, address, fn, functionName, args = [], eventKey, value }) => {
            if (!checkConnect(account))
                return;
            try {
                const writeParams = {
                    abi: abi,
                    address: address,
                    functionName: functionName || fn,
                    args: args
                };
                if (value !== undefined && value !== null) {
                    writeParams.value = typeof value === 'bigint' ? value : BigInt(value.toString());
                }
                const { request } = await simulateContract(walletConfig, writeParams);
                const hash = await writeContract(walletConfig, request);
                app.emit('WalletPilet.WritedEmit', {
                    state: 'pending',
                    eventKey: eventKey,
                    hash: hash,
                    error: null
                });
                await waitForTransactionReceipt(walletConfig, {
                    hash: hash
                });
                app.emit('WalletPilet.WritedEmit', {
                    state: 'success',
                    eventKey: eventKey,
                    hash: hash,
                    error: null
                });
            }
            catch (error) {
                app.emit('WalletPilet.WritedEmit', {
                    state: 'error',
                    eventKey: eventKey,
                    hash: null,
                    error: error.shortMessage
                });
            }
        };
        const handleSend = async ({ to, value, eventKey }) => {
            try {
                const hash = await sendTransaction(walletConfig, {
                    to: to,
                    value: parseEther(value.toString())
                });
                app.emit('WalletPilet.SendedEmit', {
                    state: 'pending',
                    eventKey: eventKey,
                    hash: hash,
                    error: null
                });
                let res = waitForTransactionReceipt(walletConfig, {
                    hash: hash,
                });
                app.emit('WalletPilet.SendedEmit', {
                    state: 'success',
                    eventKey: eventKey,
                    hash: hash,
                    error: null
                });
            }
            catch (error) {
                app.emit('WalletPilet.SendedEmit', {
                    state: 'error',
                    eventKey: eventKey,
                    hash: hash,
                    error: error.shortMessage
                });
            }
        };
        const handleMultiCall = async ({ contracts, eventKey }) => {
            try {
                const result = await multicall(walletConfig, {
                    contracts: contracts,
                });
                app.emit('WalletPilet.MultiCallEmit', {
                    state: 'success',
                    eventKey: eventKey,
                    result: result,
                    error: null
                });
            }
            catch (error) {
                app.emit('WalletPilet.MultiCallEmit', {
                    state: 'error',
                    eventKey: eventKey,
                    error: error.shortMessage
                });
            }
        };
        app.on('WalletPilet.Connect', handleConnect);
        app.on('WalletPilet.SwitchChain', handleSwitchChain);
        app.on('WalletPilet.Disconnect', handleDisconnect);
        app.on('WalletPilet.AddToken', handleAddToken);
        app.on('WalletPilet.SignMsg', handleSignMsg);
        app.on('WalletPilet.VerifyMessage', handleVerifyMessage);
        app.on('WalletPilet.GetBalance', handleGetBalance);
        app.on('WalletPilet.ReadContract', handleReadContract);
        app.on('WalletPilet.WriteContract', handleWriteContract);
        app.on('WalletPilet.Send', handleSend);
        app.on('WalletPilet.MultiCall', handleMultiCall);
        return () => {
            app.off('WalletPilet.Connect', handleConnect);
            app.off('WalletPilet.SwitchChain', handleSwitchChain);
            app.off('WalletPilet.Disconnect', handleDisconnect);
            app.off('WalletPilet.AddToken', handleAddToken);
            app.off('WalletPilet.SignMsg', handleSignMsg);
            app.off('WalletPilet.VerifyMessage', handleVerifyMessage);
            app.off('WalletPilet.GetBalance', handleGetBalance);
            app.off('WalletPilet.ReadContract', handleReadContract);
            app.off('WalletPilet.WriteContract', handleWriteContract);
            app.off('WalletPilet.Send', handleSend);
            app.off('WalletPilet.MultiCall', handleMultiCall);
        };
    }, [app, account, openConnectModal, openAccountModal, openChainModal]);
    return null;
};
export default WalletListenerComponent;
